import { useCallback, useEffect, useState } from "react";
import { Icon } from "@astrum/ui";
import { isNativeNotificationFlagged, onNativeNotificationHealthChange } from "../lib/native-notification-health.js";
import { isDesktop } from "../lib/transport.js";
import "./native-notification-warning.css";

/** Remembers a dismissal, so this is shown once per streak rather than every render. */
const DISMISSED_KEY = "astrum.native-notification-warning.dismissed";

/**
 * Tells the desktop user their OS popups are not appearing.
 *
 * Every message still lands in the in-app bell and, if configured, by email —
 * nothing about the notification itself is broken. What can silently fail is
 * only the native popup: `alerts.rs`'s `notify()` command asks macOS's
 * notification centre (or Windows' toast API) to show it, and if that
 * permission was never granted, the OS-level failure has no way to surface
 * itself anywhere the user would see it — the person just never gets a popup
 * and has no reason to suspect why, especially once email quietly fills the
 * gap. This only appears after a real streak of failures (see
 * native-notification-health.ts), not a single blip, and only in the desktop
 * build — the web notification centre has no equivalent permission gate.
 */
export function NativeNotificationWarning() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isDesktop) return;

    const sync = () => {
      if (!isNativeNotificationFlagged()) {
        setVisible(false);
        return;
      }
      let dismissed = false;
      try {
        dismissed = localStorage.getItem(DISMISSED_KEY) === "true";
      } catch {
        // Private browsing, or storage full — show it; a repeat is a smaller
        // problem than staying silent about a real gap.
      }
      setVisible(!dismissed);
    };

    sync();
    return onNativeNotificationHealthChange(sync);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // As above — it will just ask again next session.
    }
  }, []);

  if (!visible) return null;

  return (
    <div className="native-notification-warning" role="status">
      <span className="native-notification-warning__icon">
        <Icon name="bell" size={18} />
      </span>

      <div className="native-notification-warning__body">
        <strong>Desktop popups aren&rsquo;t showing</strong>
        <p>
          New messages are still arriving in your notifications and by email, but this computer isn&rsquo;t
          letting Astrum Connect show a popup. Check your system&rsquo;s notification settings — on a Mac
          that&rsquo;s System Settings → Notifications, on Windows it&rsquo;s Settings → Notifications — and
          make sure Astrum Connect is allowed.
        </p>
      </div>

      <button
        type="button"
        className="native-notification-warning__close"
        aria-label="Dismiss"
        onClick={dismiss}
      >
        <Icon name="close" size={13} />
      </button>
    </div>
  );
}
